/**
 * OFC (Open Face Chinese) Types
 *
 * Client-facing DTOs for OFC tables.
 * These types are used by both the server (for serialization) and client (for deserialization).
 */

// ============================================================================
// OFC Row and Board Types
// ============================================================================

/**
 * OFC row position
 */
export type OFCRowPosition = 'front' | 'middle' | 'back';

/**
 * OFC board data sent from server to client
 */
export interface OFCBoardDto {
  front: string[];
  middle: string[];
  back: string[];
  isComplete: boolean;
  isFouled: boolean;
  /** Slot indices that were placed in the most recent round (for "Last Set" ribbon) */
  lastSetSlots?: { front: number[]; middle: number[]; back: number[] };
}

// ============================================================================
// OFC Available Actions
// ============================================================================

/**
 * OFC available actions for a player
 */
export interface OFCAvailableActionsDto {
  canPlace: boolean;
  requiredPlacements: number;
  requiredDiscards: number;
  availablePositions: OFCRowPosition[];
  isFantasy: boolean;
  timeRemaining: number | null;
  usingTimebank: boolean;
  usingDisconnectionTimebank: boolean;
}

// ============================================================================
// OFC Player State (private to player)
// ============================================================================

/**
 * OFC player state - only sent to the player themselves
 */
export interface OFCPlayerStateDto {
  cardIndexes: number[];
  holeCards: string[]; // Cards in hand like ["Ah", "Kc", "Qd"]
  discards: string[];
  cardsRevealed: boolean;
  canPreConfirm: boolean;
  hasPreConfirmed: boolean;
  isFantasyTimerActive: boolean;
  availableActions: OFCAvailableActionsDto | null;
}

// ============================================================================
// OFC Hand State
// ============================================================================

/**
 * OFC hand state
 */
export interface OFCHandStateDto {
  status: boolean;
  roundIndex: number | null;
  playerCount: number;
}

// ============================================================================
// OFC Scoring Results
// ============================================================================

/**
 * Row result with hand label and royalty points
 */
export interface OFCRowResult {
  label: string;
  royalty: number;
}

/**
 * Scoring result for a single player
 */
export interface OFCPlayerScoringResult {
  playerIndex: number;
  score: number;
  fouled: boolean;
  rowResults: {
    front: OFCRowResult;
    middle: OFCRowResult;
    back: OFCRowResult;
  };
  fantasy: {
    qualifiesForFantasy: boolean;
    continuesFantasy: boolean;
    nextHandCardCount: number;
  };
}

/**
 * Complete scoring results for all players
 */
export interface OFCScoringResults {
  players: OFCPlayerScoringResult[];
  nextFantasyPlayers: number[];
}
